import { IChecklist2HistoryReportLineGraph } from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report';
import { FiChecklist2ReportTimePeriodType } from '@fi-shared/checklist/fi-checklist2-report/enums/fi-checklist2-report-time-period-type';
import {
    FiDateFormatOptions,
    FiMonthNoYearDateFormatOptions,
    FiShortMonthDateFormatOptions,
    FiShortWeekdayDateFormatOptions,
    FiTimeFormatOptions,
} from '@fi-shared/utils';

export const HistoryChecklistLineChartSettings = {
    pointBorderWidth: 5,
    borderRadius: 9,
    lineTension: 0.3,
    completedLineBorderColor: '#99FFA3',
    completedPointBackgroundColor: '#13CA25',
    deviationLineBorderColor: '#FF897D',
    deviationPointBackgroundColor: '#DE3730',
    pointBorderColor: '#fff',
} as const;

export const checklistHistoryChartExternalTooltipHandler = (
    context,
    dataForLineGraph: IChecklist2HistoryReportLineGraph[],
) => {
    const { chart, tooltip } = context;
    const tooltipEl = chart.canvas.parentNode.querySelector('#chart-tooltip');

    // Hide if no tooltip
    if (tooltip.opacity === 0) {
        tooltipEl.style.opacity = 0;
        return;
    }

    // Set Text
    if (tooltip.body) {
        const currentDataForGraph: IChecklist2HistoryReportLineGraph =
            dataForLineGraph[tooltip.dataPoints[0].dataIndex];

        tooltipEl.querySelector('#checklist-iterations-counter').innerText = currentDataForGraph.IterationsCount;

        const deviationsPercent: number = !currentDataForGraph.TotalCheckpointsCount
            ? 0
            : getDeviationsPercent(currentDataForGraph);
        const deviationsNumValue: string = `<span class="light-color">(${currentDataForGraph.DeviationCheckpointsCount}/${currentDataForGraph.TotalCheckpointsCount})</span>`;
        tooltipEl.querySelector('#checklist-deviations-counter').innerHTML =
            `${deviationsPercent}% ${deviationsNumValue}`;

        const completedPercent: number = !currentDataForGraph.TotalCheckpointsCount
            ? 0
            : getCompletedPercent(currentDataForGraph);
        const completedNumValue: string = `<span class="light-color">(${currentDataForGraph.FilledCheckpointsCount}/${currentDataForGraph.TotalCheckpointsCount})</span>`;
        tooltipEl.querySelector('#checklist-completed-counter').innerHTML = `${completedPercent}% ${completedNumValue}`;

        tooltipEl.querySelector('#checklist-deviations-to-completed-counter').innerHTML = getPercentageTooltipText(
            currentDataForGraph.DeviationCheckpointsCount,
            currentDataForGraph.FilledCheckpointsCount,
        );
    }

    const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

    const pointSizePx: number = 9;
    const canvasStyles: CSSStyleDeclaration = getComputedStyle(chart.canvas);
    const tooltipStyles: CSSStyleDeclaration = getComputedStyle(tooltipEl);

    const canvasWidthPx: number = +canvasStyles.width.replace('px', '');
    const tooltipWidthPx: number = +tooltipStyles.width.replace('px', '');
    let tooltipLeftOffsetPx: number;
    if (tooltipWidthPx > positionX + tooltip.caretX) {
        tooltipLeftOffsetPx = tooltipWidthPx / 2;
    } else if (positionX + tooltip.caretX > canvasWidthPx) {
        tooltipLeftOffsetPx = tooltip.caretX - (tooltip.width + positionX);
    } else {
        tooltipLeftOffsetPx = positionX + tooltip.caretX;
    }

    const canvasHeightPx: number = +canvasStyles.height.replace('px', '');
    const tooltipHeightPx: number = +tooltipStyles.height.replace('px', '');
    let tooltipTopOffsetPx: number;
    if (canvasHeightPx - (tooltipHeightPx + positionY + tooltip.caretY + pointSizePx) < 0) {
        tooltipTopOffsetPx = positionY + tooltip.caretY - pointSizePx - tooltipHeightPx;
    } else {
        tooltipTopOffsetPx = positionY + tooltip.caretY + pointSizePx;
    }

    tooltipEl.style.left = tooltipLeftOffsetPx + 'px';
    tooltipEl.style.top = tooltipTopOffsetPx + 'px';
    tooltipEl.style.opacity = 1;
};

export const getDeviationsPercent = (dataForGraph: IChecklist2HistoryReportLineGraph) => {
    return Math.round((dataForGraph.DeviationCheckpointsCount * 100) / dataForGraph.TotalCheckpointsCount);
};

export const getCompletedPercent = (dataForGraph: IChecklist2HistoryReportLineGraph) => {
    return Math.round((dataForGraph.FilledCheckpointsCount * 100) / dataForGraph.TotalCheckpointsCount);
};

const getPercentage = (numerator: number, denominator: number): number => {
    if (!denominator) {
        return 0;
    }
    return Math.round((numerator * 100) / denominator);
};

const getPercentageTooltipText = (numerator: number, denominator: number) => {
    const percentage = getPercentage(numerator, denominator);
    return `${percentage}% <span class="light-color">(${numerator}/${denominator})</span>`;
};

export const getYAxisTicksLabel = (val: number, index: number) => (index % 2 === 0 ? `${val}%` : undefined);

export const segmentDataSkippedFn = (ctx, value) => (ctx.p0.skip || ctx.p1.skip ? value : undefined);

export const getDateFormatOptionsByTimePeriodType = (dateRangeTimePeriodType: FiChecklist2ReportTimePeriodType) => {
    switch (dateRangeTimePeriodType) {
        case FiChecklist2ReportTimePeriodType.LastWeek:
            return FiShortWeekdayDateFormatOptions;
        case FiChecklist2ReportTimePeriodType.LastMonth:
            return FiMonthNoYearDateFormatOptions;
        case FiChecklist2ReportTimePeriodType.LastQuarter:
        case FiChecklist2ReportTimePeriodType.LastYear:
            return FiShortMonthDateFormatOptions;
        case FiChecklist2ReportTimePeriodType.Last24hrs:
            return FiTimeFormatOptions;
        default:
            return FiDateFormatOptions;
    }
};

export const getFilledDataBorderColorFn = (context): CanvasGradient => {
    if (!context.chart.chartArea) return;

    const {
        ctx,
        chartArea: { top, bottom },
    } = context.chart;
    const gradientBackgroundColor = ctx.createLinearGradient(0, top, 0, bottom);
    gradientBackgroundColor.addColorStop(0, 'rgba(124, 255, 137, 0.3)');
    gradientBackgroundColor.addColorStop(1, 'rgba(153, 255, 163, 0.05)');
    return gradientBackgroundColor;
};
